/**
 * Signature Service - Handles signature submissions and verification
 * This service communicates with the backend API for signature-related operations
 */

import {API_URL} from "@/services/api.service";

/**
 * Class for handling signature operations
 */
class SignatureService {
    /**
     * Submit a signature for a document
     * @param {string} token - Authentication token or document token
     * @param {string} signatureData - Base64 encoded signature image data
     * @returns {Promise<Object>} - Response from server
     */
    async submitSignature(token, signatureData) {
        if (!token) {
            throw new Error('未提供認證令牌');
        }

        if (!signatureData) {
            throw new Error('未提供簽名數據');
        }

        try {
            console.log(`提交簽名數據，使用令牌授權`);

            // Extract agreement_id from token if needed
            // Assuming token might be in format like "agreement_xxxx"
            // or already be the agreement ID itself
            const agreement_id = token.includes('agreement_') ?
                token.replace('agreement_', '') : token;

            // Convert base64 signature to blob for FormData
            const base64Data = signatureData.split(',')[1]; // Remove the data:image/png;base64, part
            const byteCharacters = atob(base64Data);
            const byteArrays = [];

            for (let i = 0; i < byteCharacters.length; i++) {
                byteArrays.push(byteCharacters.charCodeAt(i));
            }

            const blob = new Blob([new Uint8Array(byteArrays)], {type: 'image/png'});

            // Create FormData to match the FastAPI endpoint's expected format
            const formData = new FormData();
            formData.append('signature', blob, 'signature.png');

            // Make API request to the endpoint expecting the agreement_id in the URL
            const response = await fetch(`${API_URL}/agreements/${agreement_id}/sign`, {
                method: 'POST',
                body: formData
            });

            // Handle non-2xx responses
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `服務器響應錯誤，狀態碼: ${response.status}`);
            }

            // Parse and return data
            const data = await response.json();
            console.log('簽名提交成功, 協議ID:', data.agreement_id);
            return {
                agreementId: data.agreement_id,
                loanRef: data.loan_ref,
                timestamp: data.signature_timestamp
            };
        } catch (error) {
            console.error('簽名提交錯誤:', error);
            throw new Error(`簽名提交失敗: ${error.message || '未知錯誤'}`);
        }
    }
}

// Create and export a singleton instance
const signatureService = new SignatureService();
export default signatureService;